import type { ReactNode } from 'react';
import { Button } from '../../components/UI-CHAKRA/Button';
import { Box } from '../../components/UI-CHAKRA/Box';
import colorContrast from 'color-contrast';
import rightArrow from '../../assets/images/icons/right-arrow.svg';
import { ReactSVG } from 'react-svg';
import { Flex } from '../../components/UI-CHAKRA/Flex';
import { useMyCentralStore } from '../../stores/myCentralStore';
import { useDrawerStore } from '../../stores/drawerStore';
import { Tooltip } from '../../components/UI-CHAKRA/Tooltip';
import { SNCR_COLORS } from './SncrNumbering/constants';

export interface PrescriptionFooterProps {
  onCancel?(): void;
  onContinue(): void;
  continueLabel: string;
  loading: boolean;
  continueDisabled?: boolean;
  continueTooltip?: ReactNode;
  cancelDisabled?: boolean;
  cancelLabel: string;
  leftAction?: React.ReactNode;
}

export default function PrescriptionFooter(props: PrescriptionFooterProps) {
  const sideBarWidth = useMyCentralStore((state) => state.sideBarWidth);
  const { drawerOpen } = useDrawerStore();

  const onContinue = () => {
    if (props.loading) return;
    props.onContinue();
  };

  const continueButtonWidth = props.loading ? '50px' : 'auto';

  if (drawerOpen) return null;

  return (
    <Flex
      position={'fixed'}
      flexDirection={{ base: 'column-reverse', md: 'row' }}
      justifyContent={{ base: 'flex-end', md: 'flex-end' }}
      alignItems={'center'}
      bottom={'0px'}
      left={{ base: 0, md: 0 }}
      right={{ base: 0, md: sideBarWidth }}
      bg={'#FFFFFF'}
      h={'auto'}
      paddingY={'12px'}
      paddingX={{ base: '16px', md: '24px' }}
      gap={'8px'}
      boxShadow={'0px -3px 7.5px rgba(130, 130, 130, 0.2)'}
      transition={'right 0.3s ease'}
      zIndex={10}
      pointerEvents={'none'}
    >
      {props.leftAction && (
        <Box
          flex={{ base: 'none', md: 1 }}
          display={'flex'}
          alignItems={'center'}
          justifyContent={{ base: 'center', md: 'flex-start' }}
          pointerEvents={'auto'}
          width={{ base: '100%', md: 'auto' }}
        >
          {props.leftAction}
        </Box>
      )}

      <Flex
        flexDirection={'row'}
        gap={'8px'}
        width={{ base: '100%', md: 'auto' }}
        justifyContent={{ base: 'space-around', md: 'flex-end' }}
      >
        <Box minW={'120px'}>
          {props.continueLabel !== 'OK' && !!props.onCancel && (
            <Button
              height={'40px'}
              onClick={props.onCancel}
              isDisabled={props.cancelDisabled}
              borderRadius={'20px'}
              borderWidth={'2px'}
              minW={'120px'}
              display={'flex'}
              justifyContent={'center'}
              fontWeight={700}
              variant={'outline'}
              color={colorContrast('#fff', '#fff700') > 3 ? 'white' : '#1E252B'}
              pointerEvents={'auto'}
            >
              {props.cancelLabel}
            </Button>
          )}
        </Box>

        <Box display={'flex'} justifyContent={'flex-end'}>
          {props.continueLabel !== '' && (
            <Tooltip
              label={props.continueTooltip}
              isDisabled={!props.continueDisabled || !props.continueTooltip}
              hasArrow
              maxW={'212px'}
              padding={'12px'}
              placement="top"
              backgroundColor={SNCR_COLORS.primary}
              color={'#fff'}
              borderRadius="5px"
              textAlign={'start'}
            >
              <span>
                <Button
                  data-cy={'continue'}
                  height={'40px'}
                  w={continueButtonWidth}
                  borderRadius={'20px'}
                  display={'flex'}
                  justifyContent={'space-between'}
                  fontWeight={700}
                  variant={'solid'}
                  onClick={onContinue}
                  isDisabled={props.continueDisabled || props.loading}
                  isLoading={props.loading}
                  _loading={{
                    justifyContent: 'center',
                  }}
                  _disabled={
                    props.continueDisabled
                      ? { opacity: 0.4, cursor: 'not-allowed', backgroundColor: '#868E96', _hover: { backgroundColor: '#868E96' } }
                      : {}
                  }
                  transition={'all 0.6s ease'}
                  gap="10px"
                  minWidth={'134px'}
                  pointerEvents={'auto'}
                  data-testid="autorize-button"
                >
                  {props.continueLabel}
                  {!props.loading && props.continueLabel !== 'OK' && <ReactSVG src={rightArrow} />}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Flex>
    </Flex>
  );
}
